// Ministries.js
// -----------------------------------------------------------------------------
// GROW > Ministries page. Presents NHV's ministries as a warm, audience-aware
// grid of FeatureCards (each with its real image, mission, and rhythm), then a
// detail band for the Prayer Community's "What We Do" list, closing with a
// "get connected" CtaBanner.
//
// Container/presentational note: this is a presentational screen - all copy and
// images are module-local constants below; no fetching or global state.
// -----------------------------------------------------------------------------

import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';

import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import {
  Section,
  SectionHeading,
  PageHero,
  PrimaryButton,
  FeatureCard,
  CtaBanner,
  IconText,
} from '../../components/ui';

import usePageMeta from '../../hooks/usePageMeta';
import './ministries.css';
import ChildrensMinistry from '../../assets/ChildrensMinistry.jpg';
import WomensMinistry from '../../assets/WomensMinistry.png';
// Placeholder stock photo (Unsplash License, photo by Ziph) until NHV has its own.
import MensBibleStudy from '../../assets/MensBibleStudy.jpg';
// Placeholder stock photo (Unsplash License, photo by Nicolas Lobos) so the card
// doesn't repeat the hero image.
import BartonsHomeGroup from '../../assets/BartonsHomeGroup.jpg';
import PrayerCommunity from '../../assets/PrayerCommunity.jpg';
import KingsHomeGroup from '../../assets/KingsHomeGroup.jpg';

// Module-local content model. Lifted to module scope (not global) so the markup
// stays declarative and each ministry renders from one trusted source of truth.
// Scope note: MINISTRIES is a module-private constant - shared only within this
// file, never exported or mutated.
const MINISTRIES = [
  {
    id: 'womens-ministry',
    title: "Women's Ministry",
    image: WomensMinistry,
    imageAlt: "NHV Women's Ministry gathering",
    icon: <GroupsRoundedIcon />,
    body:
      'NHV Women gather throughout the year to grow stronger in our relationships ' +
      'with each other and encourage one another in drawing closer to the Lord. A ' +
      'warm, inviting circle of women of all ages who are passionate about Jesus.',
    schedule: [
      { icon: <ScheduleRoundedIcon />, text: 'Tuesday evenings, 7:00 to 8:30 pm. Bible study and prayer. Childcare available upon request.' },
      { icon: <RestaurantRoundedIcon />, text: 'Monthly potluck luncheon, every second Sunday after the morning service.' },
    ],
  },
  {
    id: 'mens-ministry',
    title: "Men's Bible Study",
    image: MensBibleStudy,
    imageAlt: 'A group of men praying together with arms around each other',
    icon: <GroupsRoundedIcon />,
    body:
      'NHV Men gather throughout the year to grow stronger in our relationships ' +
      'with each other and encourage one another in drawing closer to the Lord. A ' +
      'warm, inviting circle of men of all ages who are passionate about Jesus.',
    schedule: [
      { icon: <ScheduleRoundedIcon />, text: 'Tuesday evenings, 7:00 to 8:30 pm. Bible study and prayer. Childcare available upon request.' },
    ],
  },
  {
    id: 'kings-home-group',
    title: "Barton's Home Group",
    image: BartonsHomeGroup,
    imageAlt: "Barton's Home Group fellowship",
    icon: <HomeRoundedIcon />,
    body:
      'Experience the joy and growth that come from genuine fellowship with your ' +
      'church family. We build deeper connections, encourage one another, and ' +
      'strengthen our journey with Christ in a welcoming, relaxed atmosphere.',
    schedule: [
      { icon: <ScheduleRoundedIcon />, text: '2nd & 4th Fridays each month at 7:00pm' },
      { icon: <HomeRoundedIcon />, text: "At the Barton's home in Fate, TX" },
    ],
  },
  {
    id: 'childrens-ministry',
    title: "Children's Ministry",
    image: ChildrensMinistry,
    imageAlt: "NHV Children's Ministry",
    icon: <ChildCareRoundedIcon />,
    body:
      'Our children’s ministry helps kids grow in their faith, experience God’s ' +
      'love, and understand His redemptive story, building a strong foundation in ' +
      'Christ within a nurturing environment.',
    schedule: [
      { icon: <CheckCircleRoundedIcon />, text: 'During the sermon, kids through elementary school learn from The Gospel Project curriculum.' },
      { icon: <ChildCareRoundedIcon />, text: 'Loving nursery care provided for infants and toddlers.' },
    ],
  },
];

// Prayer Community is rendered in its own detail band because its "What We Do"
// content is a richer list than a card body. Source of truth kept local.
const PRAYER_POINTS = [
  {
    title: 'Monthly Gatherings',
    text:
      'We meet the third Sunday of every month at the church after the service, ' +
      'typically 12:30 to 2 pm, exploring new (and ancient) ways to discern God’s ' +
      'voice and enrich our prayer life.',
  },
  {
    title: 'Intercessory Prayer',
    text:
      'We faithfully lift up the needs congregants share through prayer requests. ' +
      'Confidentiality is a cornerstone of our ministry.',
  },
  {
    title: 'Prayer Ministry',
    text:
      'We provide prayer support during services and transformational prayer at ' +
      'other times of need, compassionate and confidential.',
  },
  {
    title: 'Trainings',
    text:
      'We host training sessions to empower those interested in prayer ministry, ' +
      'helping them grow in confidence and effectiveness in praying for others.',
  },
];

// Presentational helper: one ministry card. Block-scoped to this module.
const MinistryCard = ({ ministry }) => {
  // Zero-trust: never assume the content model is well-formed.
  if (!ministry || typeof ministry.title !== 'string' || ministry.title.length === 0) {
    return null;
  }
  const schedule = Array.isArray(ministry.schedule) ? ministry.schedule : [];

  return (
    <FeatureCard
      title={ministry.title}
      body={ministry.body}
      image={ministry.image}
      imageAlt={ministry.imageAlt || ministry.title}
      icon={ministry.icon}
      action={
        schedule.length > 0 ? (
          <Stack
            spacing={1.5}
            sx={{ pt: 2, borderTop: '1px solid', borderColor: 'neutral.200' }}
          >
            {schedule.map((row, index) => (
              <IconText key={index} icon={row.icon} align="start" level="body-sm">
                {row.text}
              </IconText>
            ))}
          </Stack>
        ) : null
      }
    />
  );
};

const Ministries = () => {
  usePageMeta(
    'Ministries',
    'Find your place at New Horizon Vineyard: women’s ministry, men’s Bible study, ' +
      'home groups, children’s ministry, and our prayer community.',
  );

  return (
    <Box>
      <PageHero
        image={KingsHomeGroup}
        eyebrow="Grow With Us"
        title="Ministries at New Horizon Vineyard"
        subtitle="There's a place for everyone here. Explore the ways our church family grows together."
        height="sm"
        overlay="strong"
      >
        <PrimaryButton to="/contact" color="accent">
          Get Connected
        </PrimaryButton>
      </PageHero>

      <Section bg="default">
        <SectionHeading
          align="center"
          eyebrow="Find Your People"
          title="Ways to Grow Together"
          subtitle="Ministry is one of many ways to put down roots at NHV. Keep exploring how we grow stronger together. Come as you are, and bring a friend."
        />
        <Box
          className="ministries-grid"
          sx={{
            display: 'grid',
            // Two columns keeps an even 2x2 layout for four ministries (three
            // columns left one card stranded on its own row).
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: { xs: 3, md: 4 },
            maxWidth: 960,
            mx: 'auto',
            mt: { xs: 1, md: 2 },
          }}
        >
          {MINISTRIES.map((ministry) => (
            <MinistryCard key={ministry.id} ministry={ministry} />
          ))}
        </Box>
      </Section>

      <Section bg="tint" id="prayer-community">
        <SectionHeading
          eyebrow="Prayer Community"
          title="A Culture of Transformation Through Prayer"
          subtitle="The NHV Prayer Community empowers individuals and our church family through intentional prayer, deepening our relationship with God, both individually and collectively."
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
            gap: { xs: 4, md: 6 },
            alignItems: 'center',
          }}
        >
          <Stack spacing={2.5}>
            {PRAYER_POINTS.map((point) => (
              <IconText
                key={point.title}
                icon={<VolunteerActivismRoundedIcon />}
                align="start"
                color="accent"
              >
                <Typography level="title-md" component="span" sx={{ display: 'block' }}>
                  {point.title}
                </Typography>
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                  {point.text}
                </Typography>
              </IconText>
            ))}
          </Stack>
          <Box
            component="img"
            src={PrayerCommunity}
            alt="NHV Prayer Community"
            loading="lazy"
            sx={{
              width: '100%',
              height: '100%',
              maxHeight: { md: 460 },
              objectFit: 'cover',
              borderRadius: 'lg',
              boxShadow: 'md',
            }}
          />
        </Box>
      </Section>

      <CtaBanner
        color="brand"
        eyebrow="Take a First Step"
        title="Let's get you connected"
        text="New here or ready to dig deeper? We'd love to help you find your place in our church family."
        action={
          <>
            <PrimaryButton to="/join" color="accent">
              Plan a Visit
            </PrimaryButton>
            <PrimaryButton to="/contact" inverted>
              Contact Us
            </PrimaryButton>
          </>
        }
      />
    </Box>
  );
};

export default Ministries;
